import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, User, LogOut, ChevronDown } from 'lucide-react';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
  };

  const navLinks = [
    { href: '/', label: 'Inicio', isRoute: true },
    { href: '/#analizador', label: 'Analizador', hash: 'analizador' },
    { href: '/faq', label: 'FAQ', isRoute: true },
    { href: '/blog', label: 'Blog', isRoute: true },
    { href: '/#contacto', label: 'Contacto', hash: 'contacto' },
  ];

  const handleHashClick = (e, link) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (location.pathname === '/') {
      const el = document.getElementById(link.hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollTo: link.hash } });
    }
  };

  return (
    <header className="backdrop-blur-xl bg-white/90 border-b border-slate-200 sticky top-0 z-50" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_baja-laboral-riesgos/artifacts/m7tg5pgh_logo.jpg"
              alt="PrevenciónLegal @tecnoabogado"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) =>
              link.isRoute ? (
                <Link key={link.href} to={link.href} className="nav-link text-sm" data-testid={`nav-${link.label.toLowerCase()}`}>
                  {link.label}
                </Link>
              ) : (
                <a key={link.href} href={link.href} onClick={(e) => handleHashClick(e, link)} className="nav-link text-sm" data-testid={`nav-${link.label.toLowerCase()}`}>
                  {link.label}
                </a>
              )
            )}
          </nav>

          {/* Admin menu — only visible when logged in as admin */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated && (
              <div className="relative">
                <button onClick={() => setUserMenuOpen(!userMenuOpen)} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 transition" data-testid="user-menu-btn">
                  <div className="w-8 h-8 bg-slate-200 flex items-center justify-center">
                    <User size={16} className="text-slate-600" />
                  </div>
                  <span className="text-sm font-medium text-slate-700">{user?.name}</span>
                  <ChevronDown size={14} className="text-slate-400" />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 shadow-lg" data-testid="user-menu-dropdown">
                    <div className="p-3 border-b border-slate-100">
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </div>
                    {user?.role === 'admin' && (
                      <Link to="/admin" className="block px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition" onClick={() => setUserMenuOpen(false)}>
                        Panel de administración
                      </Link>
                    )}
                    <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition" data-testid="logout-btn">
                      <LogOut size={14} /> Cerrar sesión
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200" data-testid="mobile-menu">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) =>
                link.isRoute ? (
                  <Link key={link.href} to={link.href} className="px-3 py-2 text-slate-700 hover:bg-slate-50" onClick={() => setMobileMenuOpen(false)} data-testid={`mobile-nav-${link.label.toLowerCase()}`}>
                    {link.label}
                  </Link>
                ) : (
                  <a key={link.href} href={link.href} className="px-3 py-2 text-slate-700 hover:bg-slate-50" onClick={(e) => handleHashClick(e, link)} data-testid={`mobile-nav-${link.label.toLowerCase()}`}>
                    {link.label}
                  </a>
                )
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
